import { useAuthStore } from '@/store/authStore';

// Route definitions with school slug prefix
export const ROUTES = {
  // Public routes (no school slug)
  login: () => '/auth/login',

  // Protected routes (with school slug)
  dashboard: (slug: string) => `/${slug}/dashboard`,
  gradebook: (slug: string) => `/${slug}/gradebook`,
  students: (slug: string) => `/${slug}/students`,
  studentDetail: (slug: string, id: string) => `/${slug}/students/${id}`,
  courses: (slug: string) => `/${slug}/courses`,
  courseDetail: (slug: string, id: string) => `/${slug}/courses/${id}`,
  calendar: (slug: string) => `/${slug}/calendar`,
  myGrades: (slug: string) => `/${slug}/my-grades`,
  // The year-end subject rollup (AR-12). Three entry points, one page: a student's
  // own record, a parent's child, and a staff view of a named student.
  myYearEndGrades: (slug: string) => `/${slug}/year-end-grades`,
  studentYearEndGrades: (slug: string, id: string) =>
    `/${slug}/students/${id}/year-end-grades`,
  parent: (slug: string) => `/${slug}/parent`,
  childGrades: (slug: string, studentId: string) =>
    `/${slug}/parent/children/${studentId}/grades`,
  childYearEndGrades: (slug: string, studentId: string) =>
    `/${slug}/parent/children/${studentId}/year-end-grades`,
  settings: (slug: string) => `/${slug}/settings`,
  mySubmissions: (slug: string) => `/${slug}/my-submissions`,
  myGradeChanges: (slug: string) => `/${slug}/my-grade-changes`,
  bulkImport: (slug: string) => `/${slug}/import`,
  // Admin routes
  adminApprovals: (slug: string) => `/${slug}/admin/approvals`,
  adminGradeChanges: (slug: string) => `/${slug}/admin/grade-changes`,
  adminParents: (slug: string) => `/${slug}/admin/parents`,
  adminTeachers: (slug: string) => `/${slug}/admin/teachers`,
  adminDepartments: (slug: string) => `/${slug}/admin/departments`,
  adminAcademicYears: (slug: string) => `/${slug}/admin/academic-years`,
  // A year and its terms are set up on one page, so "new" and "edit" are the
  // same screen with and without an id.
  adminAcademicYearNew: (slug: string) => `/${slug}/admin/academic-years/new`,
  adminAcademicYearSetup: (slug: string, yearId: string) =>
    `/${slug}/admin/academic-years/${yearId}`,
  adminAuditLog: (slug: string) => `/${slug}/admin/audit-log`,
  adminGradePosting: (slug: string) => `/${slug}/admin/grade-posting`,
} as const;

// Hook for getting routes with current user's school slug
export function useSchoolRoutes() {
  const user = useAuthStore((state) => state.user);
  const slug = user?.schoolSlug ?? '';

  return {
    slug,
    dashboard: () => ROUTES.dashboard(slug),
    gradebook: () => ROUTES.gradebook(slug),
    students: () => ROUTES.students(slug),
    studentDetail: (id: string) => ROUTES.studentDetail(slug, id),
    courses: () => ROUTES.courses(slug),
    courseDetail: (id: string) => ROUTES.courseDetail(slug, id),
    calendar: () => ROUTES.calendar(slug),
    myGrades: () => ROUTES.myGrades(slug),
    myYearEndGrades: () => ROUTES.myYearEndGrades(slug),
    studentYearEndGrades: (id: string) => ROUTES.studentYearEndGrades(slug, id),
    parent: () => ROUTES.parent(slug),
    childGrades: (studentId: string) => ROUTES.childGrades(slug, studentId),
    childYearEndGrades: (studentId: string) =>
      ROUTES.childYearEndGrades(slug, studentId),
    settings: () => ROUTES.settings(slug),
    mySubmissions: () => ROUTES.mySubmissions(slug),
    myGradeChanges: () => ROUTES.myGradeChanges(slug),
    bulkImport: () => ROUTES.bulkImport(slug),
    adminApprovals: () => ROUTES.adminApprovals(slug),
    adminGradeChanges: () => ROUTES.adminGradeChanges(slug),
    adminParents: () => ROUTES.adminParents(slug),
    adminTeachers: () => ROUTES.adminTeachers(slug),
    adminDepartments: () => ROUTES.adminDepartments(slug),
    adminAcademicYears: () => ROUTES.adminAcademicYears(slug),
    adminAcademicYearNew: () => ROUTES.adminAcademicYearNew(slug),
    adminAcademicYearSetup: (yearId: string) =>
      ROUTES.adminAcademicYearSetup(slug, yearId),
    adminAuditLog: () => ROUTES.adminAuditLog(slug),
    adminGradePosting: () => ROUTES.adminGradePosting(slug),
    // Login is public, no slug needed
    login: () => ROUTES.login(),
  };
}
